import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FirSanctionedCountriesService } from './fir-sanctioned-countries.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir-sanctioned-countries',
    templateUrl: './fir-sanctioned-countries.component.html',
    styleUrls: ['./fir-sanctioned-countries.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirSanctionedCountriesComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subscriptions = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  minimise = false;
  crudLoading = false;
  showFormLoader = true;
  modalTitle = 'Sanctioned Countries';

  sanctionedCountry = '';
  illegalPayments = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private sanctionedService: FirSanctionedCountriesService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetInitState();
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetInitState();
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private resetInitState(): void {
    this.isInitialized = false;
    this.isLoading = false;
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url ?? gc.api ?? '' };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = this.normalizeRowPayload(rawRow);
    this.USER_SETTINGS = shell.userSettingsData || {};
    this.SERVER_CONFIG = shell.serverConfigData || {};
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      modalScreen: mergedGp?.modalScreen,
      filterParams: fp,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
  }

  private normalizeRowPayload(rawRow: any): any {
    if (rawRow == null) {
      return {};
    }
    if (Array.isArray(rawRow)) {
      if (!rawRow.length) {
        return {};
      }
      const first = rawRow[0];
      const inner = first?.data !== undefined ? first.data : first;
      return extractObject(inner);
    }
    const ext = extractObject(rawRow);
    if (ext.data && typeof ext.data === 'object' && ext.COMBINATION_KEY == null && ext.combination_key == null) {
      return extractObject(ext.data);
    }
    return ext;
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.GENERAL_CONFIG?.api || this.baseURLs?.api;
    if (!baseUrl) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.loadFormData();
  }

  private loadFormData(): void {
    this.isLoading = true;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.GENERAL_CONFIG?.api || this.baseURLs?.api;
    const params = {
      action_code: 'h1g222',
      combination_key: this.rowData.COMBINATION_KEY,
      jcd_key: this.rowData.JCD_KEY ?? this.globalService?.globalParams?.jcd_key ?? 250
    };

    this.subscriptions.add(
      this.sanctionedService.loadJsonObject(baseUrl, params).subscribe(
        (data: any) => {
          this.isLoading = false;
          if (data.callSuccess === '1' && data.jsonObject && data.jsonObject.length > 0) {
            this.sanctionedCountry = data.jsonObject[0].SANCTIONED_COUNTRY || data.jsonObject[0].sanctioned_country || '';
            this.illegalPayments = data.jsonObject[0].ILLEGAL_PAYMENTS || data.jsonObject[0].illegal_payments || '';
          }
          this.showFormLoader = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.isLoading = false;
          console.error('Error loading sanctioned countries data:', error);
          this.showFormLoader = false;
          this.cdr.detectChanges();
        }
      )
    );
  }

  save() {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save', 'gtw-snackbar--yellow');
      return;
    }
    this.crudLoading = true;
    this.cdr.detectChanges();

    const jsonObj = [{
      sanctioned_country: this.sanctionedCountry,
      illegal_payments: this.illegalPayments
    }];
    const jsonSettings = [{
      combination_key: this.rowData.COMBINATION_KEY,
      tax_year: this.rowData.TAX_YEAR ?? this.globalService?.globalParams?.tax_year,
      scenario: this.rowData.SCENARIO ?? this.globalService?.globalParams?.scenario,
      jcd_key: this.rowData.JCD_KEY ?? this.globalService?.globalParams?.jcd_key ?? 250
    }];

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.GENERAL_CONFIG?.api || this.baseURLs?.api;

    this.subscriptions.add(
      this.sanctionedService.saveSanctionedCountries(
        baseUrl, jsonObj, jsonSettings, this.globalService, this.filterParams
      ).subscribe(
        (result: any) => {
          this.crudLoading = false;
          if (result.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Saved successfully', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Error saving', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      )
    );
  }

  reset() {
    this.resetInitState();
    this.showFormLoader = true;
    this.cdr.detectChanges();
    this.applyWorkspaceContext();
    this.tryInit();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
